import { Alert, AnimatedContentLoader, BetaPill, ElsterLogo } from '@root/components'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { useBusinessContext } from '@root/context'
import { getSettingsPageUrl } from '@root/constants'
import { useElsterCertificateStatus } from '@root/query/hooks'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import ElsterCertificateCard from './ElsterCertificateCard'
import ElsterLatestFilingCard from './ElsterLatestFilingCard'

const ElsterSettings: React.FC = () => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const { data, isLoading, isError } = useElsterCertificateStatus()
  const isMissingVatDetails =
    !business?.finanzamtsnummer ||
    !business?.finanzkasse_iban ||
    !business?.finanzkasse_recipient_name

  return (
    <SettingsSubPageLayout
      title={
        <TitleGroup>
          <ElsterLogo variant="star" height={34} />
          <Title>{t('settings.elsterSettings.header')}</Title>
          <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
        </TitleGroup>
      }
    >
      <AnimatedContentLoader
        isLoading={isLoading}
        isError={isError}
        errorDescription={t('error.oho')}
      >
        <Stack>
          {isMissingVatDetails && (
            <Alert
              type="warning"
              marginBottom={0}
              title={t('settings.elsterSettings.missingVatDetails.title')}
              description={
                <Trans
                  i18nKey="settings.elsterSettings.missingVatDetails.description"
                  components={[
                    <AlertLink
                      key="link"
                      to={getSettingsPageUrl(slug, { page: 'automation-vat-settings' })}
                    />
                  ]}
                />
              }
            />
          )}
          <ElsterCertificateCard />
          {data?.latest_filing && <ElsterLatestFilingCard filing={data.latest_filing} />}
          <Footer>
            <FooterText>{t('settings.elsterSettings.portalNote')}</FooterText>
            <PortalLink href="https://www.elster.de/" target="_blank" rel="noopener noreferrer">
              <ElsterLogo variant="star" height={12} />
              elster.de
            </PortalLink>
          </Footer>
        </Stack>
      </AnimatedContentLoader>
    </SettingsSubPageLayout>
  )
}

export default ElsterSettings

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
`

const Title = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.menuBg};
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const AlertLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const FooterText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const PortalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`
